import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Injector,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
  effect,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';

import { RouterModule } from '@angular/router';
import { Graph } from '@antv/x6';
import { DagreLayout } from '@antv/layout';
import { register as registerAngularShape } from '@antv/x6-angular-shape';

import { AiOrchestratorResult, ChatComponent, HistoryComponent, InkLayerComponent, SplitPaneComponent, SplitPaneVerticalComponent } from '@components';
import { SkeletonBlockComponent } from '@components';
import { ArchitectureDiagram, ArchitectureLayout, ArchitectureFacade, InkStroke, normalizeArchitectureDiagram } from '@state';
import { ToastService } from '@services';
import { applyJsonPatchOps, JsonPatchOp } from '../../utils/json-patch';

type ToolMode = 'select' | 'draw' | 'erase';

interface ArchitectureDecisionDraft {
  pattern: string;
  deploymentModel: string;
  integrationNotes: string;
}

@Component({
  selector: 'app-architecture',
  templateUrl: './architecture.page.html',
  styleUrls: ['./architecture.page.scss'],
  standalone: true,
  imports: [
    RouterModule,
    FormsModule,
    ChatComponent,
    HistoryComponent,
    InkLayerComponent,
    SplitPaneComponent,
    SplitPaneVerticalComponent,
    SkeletonBlockComponent,
  ],
})
export class ArchitecturePage implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('archCanvas', { static: true }) private archCanvasRef!: ElementRef<HTMLDivElement>;
  @ViewChild('archNode', { static: true }) private archNodeTpl!: TemplateRef<any>;
  // Use template ref `#architectureChat` in the template.
  @ViewChild('architectureChat') public architectureChat?: ChatComponent;

  public architectureStore = inject(ArchitectureFacade);
  private toast = inject(ToastService);
  private cdr = inject(ChangeDetectorRef);
  private graph?: Graph;
  public graphInstance: Graph | null = null;
  private resizeObserver?: ResizeObserver;
  private loadedForProjectId: string | null = null;
  private layoutSaveTimer: any = null;
  private inkSaveTimer: any = null;

  public toolMode: ToolMode = 'select';
  public inkColor = '#111827';
  public inkSize = 10;
  public applyingDecisions = false;
  public decisionsValidationError = '';
  public decisionsSuccessMessage = '';
  public hasAppliedDecisions = false;
  public architectureDecisionDraft: ArchitectureDecisionDraft = {
    pattern: '',
    deploymentModel: '',
    integrationNotes: '',
  };

  public readonly architecturePatternOptions = ['Monolith', 'Modular monolith', 'Microservices'];
  public readonly deploymentModelOptions = ['Single region cloud', 'Multi-region cloud', 'Hybrid cloud/on-prem'];

  constructor(private injector: Injector) {
    // Auto-load once the active project becomes available.
    effect(() => {
      const pid = this.architectureStore.activeProjectId();
      if (!pid) return;
      if (this.loadedForProjectId === pid) return;
      this.loadedForProjectId = pid;

      // Prevent cross-project bleed.
      this.clearDiagram();
      this.architectureStore.load();
      // load() dispatches async doc loads; the diagram() signal updates when they
      // arrive. Render the initial diagram once it's available (explicit save
      // paths render their own in-hand value). Not an effect() — this runs inside
      // a reactive context, where nested effect() creation is disallowed (NG0602).
      this.renderInitialDiagramWhenReady();
    });
  }

  private renderInitialDiagramWhenReady(attempt = 0): void {
    const diagram = this.architectureStore.diagram();
    if (diagram) {
      this.renderDiagram(diagram);
      return;
    }
    // Give up after ~6s; the canvas stays cleared (matches the old "else clear").
    if (attempt >= 40) return;
    setTimeout(() => this.renderInitialDiagramWhenReady(attempt + 1), 150);
  }

  public architectureSuggestions: any[] = [
    { icon: 'bi bi-diagram-3', prompt: 'Generate a high-level architecture diagram for my project' },
    { icon: 'bi bi-box', prompt: 'Add the main services/modules and how they communicate' },
    { icon: 'bi bi-shield-lock', prompt: 'Overlay security boundaries and trust zones' },
    { icon: 'bi bi-arrow-repeat', prompt: 'Add async flows (queues, events) where appropriate' },
  ];

  public architectureContext: any = {
    page: 'architecture',
    purpose: 'Help scaffold and refine the overall system architecture (modules, services, data stores, and interactions)',
    outputs: ['Architecture diagram', 'Mind map', 'Service map', 'Boundaries and data flows'],
  };

  get debugEnabled(): boolean {
    try {
      return typeof localStorage !== 'undefined' && localStorage.getItem('debugDefine') === '1';
    } catch {
      return false;
    }
  }

  ngOnInit(): void {}

  public onGenerateFromRepo() {
    const prompt = [
      'Generate (or refine) the architecture diagram from all connected GitHub repos.',
      '',
      'Requirements:',
      '- Treat pageContext.repoContext.github.repos as source of truth.',
      '- You MUST inspect every connected repo in pageContext.repoContext.github.repos.',
      '- For EACH repo: call github_get_tree first, then read high-signal config/deploy files with github_get_content (Dockerfile, compose, k8s, terraform, CI, package manifests).',
      '- Output a complete /diagram patch.',
      '- Include evidence grouped per repo/role in your message (site/api/agent-api or owner/repo groups).',
      '- If fewer than 2 repos are inspected while multiple repos are connected, return patches:[] and ask to retry.',
    ].join('\n');
    this.architectureChat?.sendInjectedPrompt(prompt);
  }

  get hasActiveArchitectureChat(): boolean {
    return (this.architectureStore.chatMessages()?.length ?? 0) > 0;
  }

  get canShowGenerateFromRepoButton(): boolean {
    const github = this.architectureStore.repoContext()?.github;
    const hasInstallation = (github?.installationId ?? null) !== null;
    const hasRepos = (github?.repos?.length ?? 0) > 0;
    return hasInstallation && hasRepos && !this.hasActiveArchitectureChat;
  }

  get architectureOrchestratorContext(): any {
    const definition = this.architectureStore.projectDefinition() ?? {};
    const diagram = this.architectureStore.diagram();
    const repoContext = this.architectureStore.repoContext()?.github;
    return {
      definition: {
        name: String((definition as any)?.name ?? ''),
        description: String((definition as any)?.description ?? '').slice(0, 500),
        goals: Array.isArray((definition as any)?.goals) ? (definition as any).goals.slice(0, 8) : [],
        features: Array.isArray((definition as any)?.features) ? (definition as any).features.slice(0, 10) : [],
      },
      diagram: diagram
        ? {
            version: diagram.version ?? 1,
            nodes: Array.isArray((diagram as any).nodes) ? (diagram as any).nodes.slice(0, 80) : [],
            edges: Array.isArray((diagram as any).edges) ? (diagram as any).edges.slice(0, 120) : [],
          }
        : null,
      repoContext: repoContext
        ? {
            github: {
              installationId: repoContext.installationId ?? null,
              repos: Array.isArray(repoContext.repos) ? repoContext.repos.slice(0, 10) : [],
            },
          }
        : null,
    };
  }

  private normalizeDiagramIds(diagram: ArchitectureDiagram | null): ArchitectureDiagram | null {
    const layout = this.architectureStore.layout();
    return normalizeArchitectureDiagram(diagram, { layout }).diagram;
  }

  async onChatMessagesChange(messages: Array<{ role: 'user' | 'assistant'; text: string }>) {
    await this.architectureStore.setChatMessages(messages);
  }

  async onChatMessageCreated(msg: { role: 'user' | 'assistant'; text: string }) {
    await this.architectureStore.logChatEvent(msg.text, msg.role);
  }

  async onAiResult(result: AiOrchestratorResult) {
    if (!result || String(result.page ?? '').toLowerCase() !== 'architecture') return;
    const ops = (Array.isArray(result.patches) ? result.patches : []) as JsonPatchOp[];
    if (ops.length === 0) {
      // Fallback: some models return a diagram directly instead of JSON patches.
      const raw: any = (result as any)?.raw ?? null;
      const diagram = raw?.diagram ?? raw?.value?.diagram ?? null;
      const nodes = Array.isArray(diagram?.nodes) ? diagram.nodes : null;
      const edges = Array.isArray(diagram?.edges) ? diagram.edges : null;
      if (diagram && nodes && edges) {
        const nextDiagram: ArchitectureDiagram = { version: 1, nodes, edges } as any;
        const normalized = this.normalizeDiagramIds(nextDiagram);
        await this.architectureStore.saveDiagram(normalized);
        if (this.graph && normalized) this.renderDiagram(normalized);
        this.toast.success('AI updated architecture diagram', 'Architecture');
        return;
      }

      this.toast.warning('AI response did not include diagram patches. Please try again.', 'Architecture');
      return;
    }

    const current =
      this.architectureStore.diagram() ??
      ({
        version: 1,
        nodes: [],
        edges: [],
      } as ArchitectureDiagram);

    try {
      const normalizedOps = ops.map((op: any) => {
        const path = String(op?.path ?? '');
        // Normalize if agent ever emits /nodes or /edges directly.
        if (path === '/nodes' || path.startsWith('/nodes/')) return { ...op, path: `/diagram${path}` };
        if (path === '/edges' || path.startsWith('/edges/')) return { ...op, path: `/diagram${path}` };
        return op;
      }) as JsonPatchOp[];

      const wrappedNext = applyJsonPatchOps<{ diagram: ArchitectureDiagram }>({ diagram: current }, normalizedOps);
      const nextDiagram = (wrappedNext?.diagram ?? null) as ArchitectureDiagram | null;
      const normalizedNextDiagram = this.normalizeDiagramIds(nextDiagram);

      const nextNodes = Array.isArray(normalizedNextDiagram?.nodes) ? normalizedNextDiagram!.nodes : [];
      if (nextNodes.length < 1) {
        // Recover: create a reasonable scaffold from the last user prompt + definition.
        const seed =
          [...(this.architectureStore.chatMessages() ?? [])].reverse().find((m: any) => m?.role === 'user')?.text ??
          result?.message ??
          '';
        const fallback = this.scaffoldDiagramFromSeed(seed, this.architectureStore.projectDefinition());
        if ((fallback.nodes ?? []).length > 0) {
          const normalizedFallback = this.normalizeDiagramIds(fallback) ?? fallback;
          await this.architectureStore.saveDiagram(normalizedFallback);
          if (this.graph) this.renderDiagram(normalizedFallback);
          this.toast.warning('AI returned an empty diagram; generated a starter scaffold instead.', 'Architecture');
          return;
        }

        if (this.debugEnabled) {
          console.warn('[ArchitecturePage] empty diagram from AI', { ops, normalizedOps, wrappedNext });
        }
        this.toast.error('AI returned an empty diagram. Please try again.', 'Architecture');
        return;
      }

      await this.architectureStore.saveDiagram(normalizedNextDiagram);
      if (this.graph && normalizedNextDiagram) this.renderDiagram(normalizedNextDiagram);
      this.toast.success('AI updated architecture diagram', 'Architecture');
    } catch (e: any) {
      this.toast.error(e?.message ?? 'Failed to apply architecture updates', 'Architecture');
    }
  }

  public onAddNode(kind: string = 'service') {
    const diagram = this.architectureStore.diagram() ?? ({ version: 1, nodes: [], edges: [] } as ArchitectureDiagram);
    const id = `n_${Math.random().toString(36).slice(2, 10)}`;
    const title = kind === 'service' ? 'Service' : kind === 'database' ? 'Database' : 'Node';
    const next: ArchitectureDiagram = {
      ...(diagram as any),
      version: 1,
      nodes: [...(diagram.nodes ?? []), { id, title, kind } as any],
      edges: [...(diagram.edges ?? [])],
    };
    this.architectureStore.saveDiagram(next);
    this.renderDiagram(next);
  }

  private scaffoldDiagramFromSeed(seedText: string, definition: any | null): ArchitectureDiagram {
    const seed = `${seedText ?? ''} ${JSON.stringify(definition ?? {})}`.toLowerCase();
    const wantFlutter = seed.includes('flutter');
    const wantMobile = seed.includes('mobile') || seed.includes('ios') || seed.includes('android');
    const wantNode = seed.includes('node') || seed.includes('express') || seed.includes('api');
    const wantDb = seed.includes('database') || seed.includes('postgres') || seed.includes('mysql') || seed.includes('mongodb');
    const wantStorage = seed.includes('storage') || seed.includes('s3') || seed.includes('cloud storage') || seed.includes('firebase');
    const wantQueue = seed.includes('queue') || seed.includes('event') || seed.includes('pubsub') || seed.includes('kafka');

    const nodes: any[] = [];
    const edges: any[] = [];

    const addNode = (id: string, title: string, kind: any) => {
      if (nodes.some((n) => n.id === id)) return;
      nodes.push({ id, title, kind });
    };
    const addEdge = (id: string, source: string, target: string, label: string) => {
      if (!nodes.some((n) => n.id === source) || !nodes.some((n) => n.id === target)) return;
      edges.push({ id, source, target, label });
    };

    // Defaults if we can't detect anything.
    addNode('client_app', wantFlutter ? 'Flutter Mobile App' : wantMobile ? 'Mobile App' : 'Client App', 'client');
    addNode('api_service', wantNode ? 'Node/Express API' : 'Backend API', 'service');
    addNode('db_primary', 'Primary Database', 'database');

    if (wantStorage) addNode('storage_cloud', 'Cloud Storage', 'external');
    if (wantQueue) addNode('queue_events', 'Event Queue', 'queue');
    addNode('boundary_trust', 'Trust Boundary', 'boundary');

    addEdge('e_client_api', 'client_app', 'api_service', 'HTTPS');
    addEdge('e_api_db', 'api_service', 'db_primary', 'SQL');
    if (nodes.some((n) => n.id === 'storage_cloud')) addEdge('e_api_storage', 'api_service', 'storage_cloud', 'Upload/Download');
    if (nodes.some((n) => n.id === 'queue_events')) addEdge('e_api_queue', 'api_service', 'queue_events', 'Publish events');
    addEdge('e_boundary_api', 'boundary_trust', 'api_service', 'Ingress');

    return { version: 1, nodes, edges } as any;
  }

  public onTidyLayout() {
    if (!this.graph) return;
    try {
      const hasEdges = this.graph.getEdges().length > 0;
      if (!hasEdges) return;
      const layout = new (DagreLayout as any)({
        rankdir: 'LR',
        nodesep: 80,
        ranksep: 140,
        controlPoints: true,
      });
      const model = {
        nodes: this.graph.getNodes().map((n) => {
          const size = n.size();
          return { id: n.id, width: size.width, height: size.height };
        }),
        edges: this.graph.getEdges().map((e) => ({
          source: e.getSourceCellId(),
          target: e.getTargetCellId(),
        })),
      };
      const result = (layout as any).layout(model);
      const dagreNodes = Array.isArray(result?.nodes) ? result.nodes : [];
      dagreNodes.forEach((n: any) => {
        const cell = this.graph!.getCellById(String(n.id));
        if (!cell || !('position' in cell)) return;
        const nodeSize = (cell as any).size?.() ?? { width: 240, height: 120 };
        const x = (n.x ?? 0) - nodeSize.width / 2;
        const y = (n.y ?? 0) - nodeSize.height / 2;
        (cell as any).position?.(x, y);
      });

      const positions: Record<string, { x: number; y: number }> = {};
      for (const node of this.graph.getNodes()) {
        const p = (node as any).getPosition?.() ?? (node as any).position?.();
        const x = Number(p?.x);
        const y = Number(p?.y);
        if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
        positions[String(node.id)] = { x, y };
      }
      this.scheduleSaveLayout(positions);
    } catch {
      // ignore
    }
  }

  private getTheme() {
    const root = getComputedStyle(document.documentElement);
    const varVal = (name: string, fallback: string) => (root.getPropertyValue(name) || '').trim() || fallback;
    const colors = {
      panelBg: varVal('--color-surface', '#ffffff'),
      text: varVal('--text-primary', '#0b1f35'),
      muted: varVal('--text-muted', '#64748b'),
      border: varVal('--color-border', '#e5e7eb'),
      edge: varVal('--color-border', '#cbd5e1'),
      grid: varVal('--color-border', '#e5e7eb'),
    };
    const accents = ['#1f6feb', '#7f56d9', '#16a34a', '#f97316', '#ef4444', '#0ea5e9', '#a855f7', '#22c55e'];
    return { colors, accents };
  }

  private getCanvasWidthFallback(): number {
    try {
      const w = this.archCanvasRef?.nativeElement?.getBoundingClientRect()?.width;
      if (typeof w === 'number' && Number.isFinite(w) && w > 0) return w;
    } catch {}
    return 1024;
  }

  private computeGridPositions(
    specs: Array<{ key: string; width: number; height: number }>,
    canvasWidth: number,
  ): Record<string, { x: number; y: number }> {
    const margin = 40;
    const gapX = 120;
    const gapY = 110;
    const maxWidth = Math.max(1, canvasWidth - margin * 2);
    const firstW = specs[0]?.width ?? 260;
    const cols = Math.max(1, Math.floor((maxWidth + gapX) / (firstW + gapX)));

    const pos: Record<string, { x: number; y: number }> = {};
    let col = 0;
    let x = margin;
    let y = margin;
    let rowMaxH = 0;

    for (const s of specs) {
      pos[s.key] = { x, y };
      rowMaxH = Math.max(rowMaxH, s.height);
      col++;
      if (col >= cols) {
        col = 0;
        x = margin;
        y += rowMaxH + gapY;
        rowMaxH = 0;
      } else {
        x += s.width + gapX;
      }
    }
    return pos;
  }

  private scheduleSaveLayout(positions: Record<string, { x: number; y: number }>): void {
    if (this.layoutSaveTimer) clearTimeout(this.layoutSaveTimer);
    this.layoutSaveTimer = setTimeout(() => {
      this.layoutSaveTimer = null;
      void this.architectureStore.saveLayout({ positions } as ArchitectureLayout);
    }, 350);
  }

  public onInkStrokesChange(strokes: InkStroke[]): void {
    const next = Array.isArray(strokes) ? strokes : [];
    // Update UI immediately; debounce persistence to backend.
    void this.architectureStore.setInkDraft({ strokes: next });
    if (this.inkSaveTimer) clearTimeout(this.inkSaveTimer);
    this.inkSaveTimer = setTimeout(() => {
      this.inkSaveTimer = null;
      void this.architectureStore.saveInk({ strokes: next });
    }, 350);
  }

  private renderDiagram(diagram: ArchitectureDiagram): void {
    if (!this.graph) return;
    const { colors, accents } = this.getTheme();

    try {
      this.graph.clearCells();
    } catch {
      (this.graph as any).clearCells?.();
    }

    const nodes = Array.isArray(diagram?.nodes) ? diagram.nodes : [];
    const edges = Array.isArray(diagram?.edges) ? diagram.edges : [];
    if (nodes.length === 0) {
      (this.graph as any).centerContent?.();
      return;
    }

    const storedPositions = this.architectureStore.layout()?.positions ?? {};
    const canvasWidth = this.getCanvasWidthFallback();

    const nodeSpecs = nodes.map((n, idx) => {
      const width = 260;
      const height = 140;
      return { id: String((n as any)?.id ?? ''), width, height, accent: accents[idx % accents.length]! };
    }).filter((s) => !!s.id);

    const missingSpecs = nodeSpecs
      .filter((s) => {
        const p = storedPositions[s.id];
        const x = Number(p?.x);
        const y = Number(p?.y);
        return !(Number.isFinite(x) && Number.isFinite(y));
      })
      .map((s) => ({ key: s.id, width: s.width, height: s.height }));

    const gridPositions = this.computeGridPositions(missingSpecs, canvasWidth);

    const nodeById = new Map<string, any>();
    for (const [idx, n] of nodes.entries()) {
      const id = String((n as any)?.id ?? '').trim();
      if (!id) continue;
      const saved = storedPositions[id];
      const sx = Number(saved?.x);
      const sy = Number(saved?.y);
      const grid = gridPositions[id];
      const x = Number.isFinite(sx) ? sx : Number(grid?.x ?? 40);
      const y = Number.isFinite(sy) ? sy : Number(grid?.y ?? 40);
      const accent = accents[idx % accents.length]!;

      const title = String((n as any)?.title ?? (n as any)?.name ?? 'Node');
      const kind = String((n as any)?.kind ?? 'service');
      const node = this.graph.addNode({
        id,
        x,
        y,
        shape: 'arch-angular-node',
        width: 260,
        height: 140,
        data: { ngArguments: { title, kind, accent }, nodeId: id },
      } as any);
      nodeById.set(id, node);
    }

    for (const e of edges) {
      const source = String((e as any)?.source ?? '').trim();
      const target = String((e as any)?.target ?? '').trim();
      if (!source || !target) continue;
      const sourceNode = nodeById.get(source);
      const targetNode = nodeById.get(target);
      if (!sourceNode || !targetNode) continue;
      const labelText = String((e as any)?.label ?? '').trim();

      this.graph.addEdge({
        id: String((e as any)?.id ?? `e_${source}_${target}`),
        source: sourceNode,
        target: targetNode,
        attrs: {
          line: {
            stroke: colors.edge,
            strokeWidth: 1.6,
            targetMarker: { name: 'block', width: 10, height: 8 },
          },
        },
        labels: labelText
          ? [
              {
                markup: [{ tagName: 'rect', selector: 'body' }, { tagName: 'text', selector: 'label' }],
                attrs: {
                  body: { ref: 'label', refWidth: '120%', refHeight: '120%', refX: '-10%', refY: '-10%', rx: 6, ry: 6, fill: colors.panelBg, stroke: colors.edge, strokeWidth: 1 },
                  label: { text: labelText, fontSize: 11, fill: colors.text },
                },
              },
            ]
          : [],
      } as any);
    }

    try {
      this.graph.getEdges().forEach((ed) => ed.setRouter('orth'));
      (this.graph as any).centerContent?.();
      (this.graph as any).zoomToFit?.({ padding: 40, maxScale: 1 });
    } catch {}

    // Persist final positions so refresh restores layout.
    const nextPositions: Record<string, { x: number; y: number }> = { ...storedPositions };
    for (const [id, node] of nodeById.entries()) {
      const p = (node as any).getPosition?.() ?? (node as any).position?.();
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) continue;
      nextPositions[id] = { x, y };
    }
    this.scheduleSaveLayout(nextPositions);
  }

  private clearDiagram(): void {
    if (!this.graph) return;
    try {
      this.graph.clearCells();
    } catch {
      (this.graph as any).clearCells?.();
    }
    (this.graph as any).centerContent?.();
  }

  ngAfterViewInit(): void {
    const container = this.archCanvasRef?.nativeElement;
    if (!container) return;
    const { colors } = this.getTheme();

    this.graph = new Graph({
      container,
      background: { color: 'transparent' },
      grid: { size: 10, visible: true, type: 'dot', args: { color: colors.grid, thickness: 1 } },
      panning: true,
      mousewheel: { enabled: true, modifiers: 'ctrl', zoomAtMousePosition: true },
      connecting: {
        router: 'orth',
        connector: { name: 'rounded', args: { radius: 10 } },
        allowBlank: false,
        allowLoop: false,
        snap: true,
        highlight: true,
      },
    });
    this.graphInstance = this.graph;
    // Avoid dev-mode NG0100 by syncing the binding after view init.
    this.cdr.detectChanges();

    // Persist manual node arrangement (dragging).
    const onNodeMoved = ({ node }: any) => {
      if (!node) return;
      const id = String(node.id ?? '');
      if (!id) return;
      const p = node.getPosition?.() ?? node.position?.();
      const x = Number(p?.x);
      const y = Number(p?.y);
      if (!Number.isFinite(x) || !Number.isFinite(y)) return;
      const stored = this.architectureStore.layout()?.positions ?? {};
      const next = { ...stored, [id]: { x, y } };
      this.scheduleSaveLayout(next);
    };
    try {
      (this.graph as any).on?.('node:moved', onNodeMoved);
      (this.graph as any).on?.('node:change:position', onNodeMoved);
    } catch {}

    // Very small UX: double-click a node to rename it.
    try {
      (this.graph as any).on?.('node:dblclick', ({ node }: any) => {
        const id = String(node?.id ?? '').trim();
        if (!id) return;
        const diagram = this.architectureStore.diagram();
        if (!diagram) return;
        const cur = (diagram.nodes ?? []).find((n: any) => String(n?.id ?? '') === id);
        const prevTitle = String(cur?.title ?? '');
        const nextTitle = (window as any)?.prompt?.('Rename node', prevTitle) ?? null;
        if (typeof nextTitle !== 'string') return;
        const trimmed = nextTitle.trim();
        if (!trimmed) return;
        const next = {
          ...diagram,
          nodes: (diagram.nodes ?? []).map((n: any) => (String(n?.id ?? '') === id ? { ...n, title: trimmed } : n)),
        } as ArchitectureDiagram;
        this.architectureStore.saveDiagram(next);
    this.renderDiagram(next);
      });
    } catch {}

    const resizeToContainer = () => {
      if (!this.graph) return;
      const { width, height } = container.getBoundingClientRect();
      const w = Math.max(1, Math.floor(width));
      const h = Math.max(1, Math.floor(height));
      try {
        this.graph.resize(w, h);
      } catch {
        (this.graph as any).resize?.(w, h);
      }
    };
    this.resizeObserver = new ResizeObserver(() => resizeToContainer());
    this.resizeObserver.observe(container);
    queueMicrotask(() => resizeToContainer());

    registerAngularShape({
      shape: 'arch-angular-node',
      width: 260,
      height: 140,
      injector: this.injector,
      content: this.archNodeTpl,
    });

    const diagram = this.architectureStore.diagram();
    if (diagram) this.renderDiagram(diagram);
    else this.clearDiagram();
  }

  ngOnDestroy(): void {
    if (this.layoutSaveTimer) clearTimeout(this.layoutSaveTimer);
    if (this.inkSaveTimer) clearTimeout(this.inkSaveTimer);
    this.resizeObserver?.disconnect();
    this.graph?.dispose();
    this.graphInstance = null;
  }

  public async applyArchitectureDecisions(): Promise<void> {
    this.decisionsValidationError = '';
    this.decisionsSuccessMessage = '';

    if (!this.architectureDecisionDraft.pattern.trim()) {
      this.decisionsValidationError = 'Choose an architecture pattern before applying decisions.';
      this.hasAppliedDecisions = false;
      return;
    }

    if (!this.architectureDecisionDraft.deploymentModel.trim()) {
      this.decisionsValidationError = 'Choose a deployment model before applying decisions.';
      this.hasAppliedDecisions = false;
      return;
    }

    this.applyingDecisions = true;
    try {
      // Local simulation of apply step for clear loading/success states.
      await new Promise((resolve) => setTimeout(resolve, 700));
      this.hasAppliedDecisions = true;
      this.decisionsSuccessMessage = 'Architecture decisions applied to the system outline.';
    } finally {
      this.applyingDecisions = false;
    }
  }
}

